// scheduling/components/idd/IDDDatesTable.jsx
// Step 2 of the guided flow: one dates decision per job (the internal due dates: when the
// work should start and be done). Claude's suggestion sits beside the date inputs;
// Accept / Keep save right away, typed dates save with Save, Change undoes. The focus job
// (hovered row or bar, else the selected row) is gold here, on the timeline and on the
// histogram; the selected row keeps a gold edge while another is hovered.
import { Fragment, useEffect, useRef } from 'react';
import { fmtHours, fmtDay, fmtRange, fmtDateInput } from './iddFormat';
import IDDRowMenu from './IDDRowMenu';
import { undoable, fsDependency, startsBeforeFS } from '../../utils/iddSteps';

const KIND_LABEL = { ai: 'AI', keep: 'kept', typed: 'entered' };

// "After FS" under a return that waits for its financial statements (#743).
function fsTitle(fs) {
  return `${fs.name}${fs.manager ? ` (${fs.manager})` : ''}`
    + `${fs.linked ? '' : ' · found by same client and year end'}. This return starts after the FS is finished.`
    + `${!fs.decided && fs.end ? ` Its IDD isn't decided yet; Practice Engine has it ending ${fmtDay(fs.end)}.` : ''}`;
}

export default function IDDDatesTable({
  jobs, info, edits, ai, staleAI, selection, onToggle, onToggleAll, selectedJob, onSelect,
  focusJob, onHover, onDecide, onSaveTyped, onCancelTyped, onEditDate, onUndo, onGoBudget,
  kinds, readOnly, showManager, menuFor, sortCol, sortDir, onSort, busy,
}) {
  // A bar clicked on the timeline brings its row into view.
  const rowRefs = useRef({});
  useEffect(() => {
    const el = selectedJob && rowRefs.current[selectedJob];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedJob]);
  // Undecided rows tick for a decision, decided drafts for the bulk Undo.
  const canTick = (j) => {
    const r = info[j.PEJobIndex];
    // a decision needs the job's budget first; a decided row can always be undone
    return !!r && !readOnly && !r.locked && (r.datesDecided ? undoable(j, 'dates') : r.decided);
  };
  const selectable = jobs.filter(canTick);
  const allOn = selectable.length > 0 && selectable.every(j => selection.has(j.PEJobIndex));
  const colCount = showManager ? 10 : 9;
  const Th = ({ col, label, style, title }) => (
    <th className={`idd-th${col ? '' : ' idd-th--static'}`} style={style} title={title} onClick={col ? () => onSort(col) : undefined}>
      {label}{col && sortCol === col && <span className="idd-sort-icon">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
    </th>
  );

  return (
    <div className="idd-table-wrap">
      <table className="idd-table idd-table--steps">
        <thead>
          <tr>
            <th className="idd-th idd-th--static idd-td--check">
              <input type="checkbox" aria-label="Select all shown" checked={allOn} disabled={selectable.length === 0}
                onChange={() => onToggleAll(allOn ? [] : selectable.map(j => j.PEJobIndex))} />
            </th>
            <Th col="clientCode" label="Client" />
            <Th col="jobName" label="Job" style={{ minWidth: 170 }} />
            {showManager && <Th col="manager" label="Manager" />}
            <Th col="budget" label="Budget" title="The budget from step 1 (the AI dates are built on it)" />
            <Th label="AI suggests" title="Claude's start and due dates, spreading your jobs so no week is overloaded and keeping last year's order" />
            <Th label="Start" />
            <Th label="Due" />
            <Th label="Decision" style={{ minWidth: 200 }} />
            <th className="idd-th idd-th--static" style={{ width: 34 }} aria-label="More" />
          </tr>
        </thead>
        <tbody>
          {jobs.length === 0 && <tr><td colSpan={colCount} className="idd-empty">Nothing to show with these filters.</td></tr>}
          {jobs.map(job => {
            const pe = job.PEJobIndex;
            const r = info[pe];
            if (!r) return null;
            const e = edits[pe] || {};
            const dirty = e.startDeadline != null || e.deadline != null;
            const rec = ai[pe];
            const start = e.startDeadline ?? job.NewStartDeadline ?? job.StartDeadline ?? null;
            const due = e.deadline ?? job.NewDeadline ?? job.Deadline ?? null;
            const sStr = start ? String(start).slice(0, 10) : '';
            const dStr = due ? String(due).slice(0, 10) : '';
            const valid = !!sStr && !!dStr && dStr > sStr;
            // No dates before the job's budget (a job that joined after the rest were done).
            const needsBudget = !r.decided && !r.datesDecided;
            const locked = readOnly || r.locked || needsBudget;
            const kind = kinds[pe];
            const fs = fsDependency(job);
            return (
              <Fragment key={pe}>
                <tr
                  ref={el => { rowRefs.current[pe] = el; }}
                  className={`idd-row idd-row--job${r.ready ? ' idd-row--ready' : ''}${dirty ? ' idd-row--dirty' : ''}${selectedJob === pe ? ' idd-row--selected' : ''}${focusJob === pe ? ' idd-row--focus' : ''}`}
                  onClick={() => onSelect(pe)}
                  onMouseEnter={() => onHover(pe)}
                  onMouseLeave={() => onHover(null)}
                >
                  <td className="idd-td idd-td--check" onClick={ev => ev.stopPropagation()}>
                    <input type="checkbox" aria-label={`Select ${job.ClientCode}`} checked={selection.has(pe)}
                      disabled={!canTick(job)} onChange={() => onToggle(pe)} />
                  </td>
                  <td className="idd-td">
                    <div className="idd-cell-main">{job.ClientCode}</div>
                    <div className="idd-cell-sub">{job.ClientName}</div>
                  </td>
                  <td className="idd-td">
                    <div className="idd-cell-main">{job.JobName}</div>
                    {job.ServiceDesc && <div className="idd-cell-sub">{job.ServiceDesc}</div>}
                    {fs && (
                      <div className={`idd-fs-dep${fs.decided ? '' : ' idd-fs-dep--open'}`} title={fsTitle(fs)}>
                        After FS: {fs.decided && fs.end ? `ends ${fmtDay(fs.end)}` : 'dates not set yet'}
                      </div>
                    )}
                  </td>
                  {showManager && <td className="idd-td">{job.ManagerName}</td>}
                  <td className="idd-td idd-td--num">
                    {r.decided ? <strong>{fmtHours(r.decidedHours ?? r.base)}h</strong> : (
                      <button className="idd-dec-link" onClick={ev => { ev.stopPropagation(); onGoBudget(pe); }}
                        title="This job's budget isn't decided yet (step 1)">set budget</button>
                    )}
                  </td>
                  <td className="idd-td idd-td--num">
                    {rec ? (
                      <span title={rec.Optimization_Logic || "Claude's suggestion: it spreads your jobs so no week is overloaded, keeps last year's order, and never starts before the tax year ends."}>
                        <span className="idd-sugg-ai">{fmtRange(rec.startdate, rec.enddate)}</span>
                        {staleAI.has(pe) && <div className="idd-sugg-warn" title="This job's budget changed since these dates were made. Refresh AI dates above.">budget changed</div>}
                        {startsBeforeFS(job, rec.startdate) && <div className="idd-sugg-warn" title={`Made before the FS dates were decided. Refresh AI dates above to start after ${fmtDay(fs.end)}.`}>starts before FS ends</div>}
                      </span>
                    ) : <span className="idd-cell-sub">—</span>}
                    {job.PYCompletion && <div className="idd-cell-sub">last year done {fmtDay(job.PYCompletion)}</div>}
                  </td>
                  <td className="idd-td idd-td--input" onClick={ev => ev.stopPropagation()}>
                    <input type="date" className="idd-input idd-input--date" value={fmtDateInput(start)} disabled={locked}
                      aria-label={`Start for ${job.ClientCode}`} onChange={ev => onEditDate(pe, 'startDeadline', ev.target.value || null)} />
                    {startsBeforeFS(job, sStr) && <div className="idd-sugg-warn">before FS ends {fmtDay(fs.end)}</div>}
                  </td>
                  <td className="idd-td idd-td--input" onClick={ev => ev.stopPropagation()}>
                    <input type="date" className="idd-input idd-input--date" value={fmtDateInput(due)} disabled={locked}
                      aria-label={`Due for ${job.ClientCode}`} onChange={ev => onEditDate(pe, 'deadline', ev.target.value || null)} />
                    {sStr && dStr && !valid && <div className="idd-sugg-warn">due must be after start</div>}
                  </td>
                  <td className="idd-td idd-td--decision" onClick={ev => ev.stopPropagation()}>
                    {job.ReviewStatus === 'submitted' ? <span className="idd-dec-done">Submitted</span>
                      : job.RemoveAction ? <span className="idd-dec-removed">{job.RemoveAction === 'close_no_roll' ? 'Closed, not rolling' : 'Not doing this year'}</span>
                      : readOnly ? <span className="idd-dec-done">{fmtRange(start, due)}</span>
                      : needsBudget ? (
                        <button className="idd-dec-link" onClick={() => onGoBudget(pe)} title="Decide this job's budget first; its dates unlock after">
                          Budget first
                        </button>
                      )
                      : dirty ? (
                        <span className="idd-dec-group">
                          <button className="idd-dec-btn idd-dec-btn--dates idd-dec-btn--primary" disabled={busy || !valid}
                            title={valid ? 'Save these dates' : 'Pick a start and a due date after it'}
                            onClick={() => onSaveTyped(pe)}>Save dates</button>
                          <button className="idd-dec-link" onClick={() => onCancelTyped(pe)}>Cancel</button>
                        </span>
                      ) : r.datesDecided ? (
                        <span className="idd-dec-group">
                          <span className="idd-dec-done">
                            ✓ {job.ReviewStatus === 'confirmed' ? 'Confirmed' : fmtRange(job.NewStartDeadline, job.NewDeadline)}
                            {kind && <span className="idd-dec-kind"> · {KIND_LABEL[kind] || kind}</span>}
                          </span>
                          {job.ReviewStatus === 'draft' && (
                            <button className="idd-dec-link" disabled={busy} onClick={() => onUndo(pe)} title="Undo this dates decision">Change</button>
                          )}
                        </span>
                      ) : r.dateOptions.length === 0 ? (
                        <span className="idd-cell-sub">Pick a start and due date</span>
                      ) : (
                        <span className="idd-dec-group">
                          {r.dateOptions.map((o, i) => (
                            <button key={o.kind}
                              className={`idd-dec-btn idd-dec-btn--dates${i === 0 ? ' idd-dec-btn--primary' : ''}`}
                              disabled={busy}
                              title={o.kind === 'ai' ? `Use Claude's ${fmtRange(o.startdate, o.enddate)}` : `Keep the dates Practice Engine has: ${fmtRange(o.startdate, o.enddate)}`}
                              onClick={() => onDecide(pe, o)}>
                              {o.kind === 'ai' ? 'Accept AI dates' : `Keep ${fmtRange(o.startdate, o.enddate)}`}
                            </button>
                          ))}
                        </span>
                      )}
                  </td>
                  <td className="idd-td" onClick={ev => ev.stopPropagation()}><IDDRowMenu items={menuFor(job)} /></td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
